/**
 * SettingsMenu
 * ------------
 * The cog button + collapsible options panel that used to live in Hud.
 * Extracted into its own component so NavOverlay can host it next to
 * the search icon (matching user-requested top-right grouping) and so
 * Hud stays focused on brand + mode toggles.
 *
 * Internal state:
 *   - `open` (boolean) controls the panel visibility. Defaulted closed
 *     so the canvas opens with a quiet HUD; the user expands when they
 *     want filter controls.
 *
 * Layout:
 *   - The cog button is a 28x28 icon that sits inline. NavOverlay
 *     positions it next to the search icon.
 *   - The panel is `position: fixed` so it floats over the canvas
 *     regardless of where the button itself lives. Anchors to the
 *     top-right of the viewport because that's where the trigger sits.
 *   - On mobile portrait the panel spans most of the screen width;
 *     on desktop it sizes to its content (no horizontal scroll).
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../state/store';
import { DIFFICULTY_ORDER } from '../types/domain';
import { downloadDataExport } from './data-export';
import { SEEN_TOUR, flag } from './onboarding';
import { useViewport } from './useViewport';
import { FONT, GLASS, SURFACE, LINE, LINE2, INK, INK2, INK3, BLUE, RADIUS, hexA, activePill } from './tokens';

export function SettingsMenu() {
  const mode = useAppStore((s) => s.mode);
  const setMode = useAppStore((s) => s.setMode);
  const showInterceptions = useAppStore((s) => s.showInterceptions);
  const setShowInterceptions = useAppStore((s) => s.setShowInterceptions);
  const showEdgeDirection = useAppStore((s) => s.showEdgeDirection);
  const setShowEdgeDirection = useAppStore((s) => s.setShowEdgeDirection);
  const showEdges = useAppStore((s) => s.showEdges);
  const setShowEdges = useAppStore((s) => s.setShowEdges);
  const giFilter = useAppStore((s) => s.giFilter);
  const setGiFilter = useAppStore((s) => s.setGiFilter);
  const autorotatePaused = useAppStore((s) => s.autorotatePaused);
  const setAutorotatePaused = useAppStore((s) => s.setAutorotatePaused);
  // mode + setMode already pulled at the top of this function so we
  // can render the canvas-mode pills inside the settings panel.
  const clusterBy = useAppStore((s) => s.clusterBy);
  const setClusterBy = useAppStore((s) => s.setClusterBy);
  const freeLook = useAppStore((s) => s.freeLook);
  const setFreeLook = useAppStore((s) => s.setFreeLook);
  const sizeBy = useAppStore((s) => s.sizeBy);
  const setSizeBy = useAppStore((s) => s.setSizeBy);
  const familySubClusters = useAppStore((s) => s.familySubClusters);
  const setFamilySubClusters = useAppStore((s) => s.setFamilySubClusters);
  const sleevesExpanded = useAppStore((s) => s.sleevesExpanded);
  const setSleevesExpanded = useAppStore((s) => s.setSleevesExpanded);
  const difficultyFilter = useAppStore((s) => s.difficultyFilter);
  const toggleDifficulty = useAppStore((s) => s.toggleDifficulty);
  const collapseVariants = useAppStore((s) => s.collapseVariants);
  const setCollapseVariants = useAppStore((s) => s.setCollapseVariants);
  const clusterStrength = useAppStore((s) => s.clusterStrength);
  const setClusterStrength = useAppStore((s) => s.setClusterStrength);
  const showMovesFrom = useAppStore((s) => s.showMovesFrom);
  const setShowMovesFrom = useAppStore((s) => s.setShowMovesFrom);
  const showMovesTo = useAppStore((s) => s.showMovesTo);
  const setShowMovesTo = useAppStore((s) => s.setShowMovesTo);

  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { isMobile, isPortrait } = useViewport();
  const compactPortrait = isMobile && isPortrait;
  // Onboarding tour flashes the gear on its "settings" step.
  const flashGear =
    useAppStore((s) => s.coachHighlight) === 'settings'
      ? 'bjjmap-ctrl-flash 2.2s ease-in-out infinite'
      : undefined;

  return (
    <>
      <button
        data-tour="settings"
        onClick={() => setOpen((v) => !v)}
        aria-label="View options"
        aria-expanded={open}
        title="View options"
        style={{
          width: 36,
          height: 36,
          padding: 0,
          background: open ? hexA(BLUE, 0.14) : GLASS.panel,
          color: INK,
          border: open
            ? `1px solid ${hexA(BLUE, 0.32)}`
            : `1px solid ${LINE2}`,
          borderRadius: RADIUS.sm,
          cursor: 'pointer',
          lineHeight: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(6px)',
          transition: 'background 120ms ease, border-color 120ms ease',
          animation: flashGear,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && createPortal(
        // Portal the panel to <body> so it ESCAPES NavOverlay's
        // stacking context (NavOverlay's outer div uses
        // zIndex: 30, which would otherwise trap any descendant
        // — including this panel — at that level even with a
        // higher inline z-index). Living at the document root
        // lets the panel's zIndex 1000 compare directly against
        // the in-canvas drei <Html> overlays (max z 100), so
        // cluster / hub / zone labels can no longer paint over
        // the menu's opaque #11141d background.
        <div
          // Esc-to-close lives on this wrapper. We deliberately do
          // NOT install an outside-pointer listener — interacting
          // with the canvas (tap a node, drag to orbit, pinch to
          // zoom) should keep the menu open so the user can flip
          // options and immediately see the result. The cog button
          // (top-right) remains the explicit toggle.
          ref={(el) => {
            if (!el) return;
            // Focus so Esc-key handler captures here. The autofocus
            // is harmless: the panel doesn't trap focus and the
            // user can keep interacting with the canvas freely.
            el.focus({ preventScroll: true });
          }}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
            }
          }}
          data-bjj-settings-panel="true"
          style={{
            position: 'fixed',
            top: 60,
            // Anchor the panel's right edge to the canvas pane's right
            // edge, NOT the viewport. NavOverlay (and therefore the cog
            // button) lives inside the canvas pane and shifts left as
            // the drawer widens; the panel needs to track the same
            // boundary so it stays under the cog instead of getting
            // covered by the drawer. Uses the same CSS variable that
            // EdgeActions / PanelHandle / Hud-hint key off, so the
            // four overlay layers all slide as a group during a resize.
            // In bottom-sheet mode the side drawer is closed (variable
            // is 0px there) so this reduces to `right: 14` — the same
            // as before.
            right: 'calc(var(--bjjmap-drawer-width, 0px) + 14px)',
            // Match the drawer's open/resize easing so the panel
            // slides with it instead of jumping.
            transition: 'right 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            width: compactPortrait ? 'calc(100vw - 28px)' : 'auto',
            minWidth: 260,
            maxWidth: compactPortrait ? undefined : 'min(420px, 90vw)',
            maxHeight: compactPortrait ? '45vh' : '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            // Sit ABOVE every in-canvas drei <Html> overlay. The
            // graph layer renders cluster / hub / proximity /
            // hover-card labels via drei Html with zIndexRange
            // tops of 55 / 80 / 70 / 100 respectively.
            zIndex: 1000,
            // Fully opaque background so option pills + labels stay
            // readable when the 3D scene churns behind the menu.
            background: SURFACE.raised,
            border: `1px solid ${LINE2}`,
            borderRadius: RADIUS.lg,
            padding: '12px 14px 14px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            outline: 'none',
          }}
        >
            {/* Canvas mode (positions vs moves as nodes). Moved into
                the settings panel so the HUD's top-left brand row can
                shed clutter. */}
            <Section label="canvas mode">
              <Pill active={mode === 'position'} onClick={() => setMode('position')}>
                positions
              </Pill>
              <Pill active={mode === 'move'} onClick={() => setMode('move')}>
                moves
              </Pill>
            </Section>

            {/* gi / no-gi filter — each pill cycles through 3 states on
                repeat clicks: all → gi → gi-only → all (and the same
                for no-gi). */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <Pill active={giFilter === 'all'} onClick={() => setGiFilter('all')}>all</Pill>
              <Pill
                active={giFilter === 'gi' || giFilter === 'gi-only'}
                onClick={() =>
                  setGiFilter(
                    giFilter === 'gi'
                      ? 'gi-only'
                      : giFilter === 'gi-only'
                        ? 'all'
                        : 'gi',
                  )
                }
              >
                {giFilter === 'gi-only' ? 'gi-only' : 'gi'}
              </Pill>
              <Pill
                active={giFilter === 'no-gi' || giFilter === 'no-gi-only'}
                onClick={() =>
                  setGiFilter(
                    giFilter === 'no-gi'
                      ? 'no-gi-only'
                      : giFilter === 'no-gi-only'
                        ? 'all'
                        : 'no-gi',
                  )
                }
              >
                {giFilter === 'no-gi-only' ? 'no-gi-only' : 'no-gi'}
              </Pill>
            </div>

            {/* Scene state */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              <Pill
                active={!autorotatePaused}
                onClick={() => setAutorotatePaused(!autorotatePaused)}
              >
                rotation: {autorotatePaused ? 'off' : 'on'}
              </Pill>
              <Pill
                active={!collapseVariants}
                onClick={() => setCollapseVariants(!collapseVariants)}
              >
                variants: {collapseVariants ? 'collapsed' : 'shown'}
              </Pill>
            </div>

            {/* Edges */}
            <Section label="edges">
              <Pill active={showEdges} onClick={() => setShowEdges(!showEdges)}>
                edges: {showEdges ? 'on' : 'off'}
              </Pill>
              <Pill
                active={showInterceptions}
                onClick={() => setShowInterceptions(!showInterceptions)}
              >
                counters: {showInterceptions ? 'on' : 'off'}
              </Pill>
              <Pill
                active={showEdgeDirection}
                onClick={() => setShowEdgeDirection(!showEdgeDirection)}
              >
                direction: {showEdgeDirection ? 'on' : 'off'}
              </Pill>
              <Pill
                active={sleevesExpanded === 'expanded'}
                onClick={() =>
                  setSleevesExpanded(
                    sleevesExpanded === 'expanded' ? 'collapsed' : 'expanded',
                  )
                }
              >
                sleeves: {sleevesExpanded}
              </Pill>
            </Section>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              <Pill active={showMovesFrom} onClick={() => setShowMovesFrom(!showMovesFrom)}>
                moves from: {showMovesFrom ? 'on' : 'off'}
              </Pill>
              <Pill active={showMovesTo} onClick={() => setShowMovesTo(!showMovesTo)}>
                moves to: {showMovesTo ? 'on' : 'off'}
              </Pill>
            </div>


            {/*
              Node size is a claim about importance, and which claim is
              right depends on what you are looking for — so it is a
              control rather than a constant.

                degree   raw connection count on a log curve. Truthful
                         about absolutes, but it was tuned when the
                         busiest node had 69 connections and `standing`
                         now has 205, so the top of the range runs hot.
                rank     the node's percentile instead. Scale-free: the
                         busiest node is the same size whatever the graph
                         grows to, and it never needs retuning.
                flat     size carries nothing. The honest way to read
                         structure without the eye being pulled to hubs.
            */}
            {/*
              Free look releases the camera from the selection. Sits above
              the lenses because it changes how you MOVE rather than what
              you see, and people reach for it when the tethered camera is
              fighting them.
            */}
            <Section label="camera">
              <Pill active={freeLook} onClick={() => setFreeLook(!freeLook)}>
                free look: {freeLook ? 'on' : 'off'}
              </Pill>
            </Section>
            {/*
              Shown only while the mode is on. Keyboard flight is
              completely undiscoverable otherwise — there is nothing on
              screen to suggest the arrow keys do anything — and printing
              it when the mode is off would be noise about a feature that
              currently does nothing.
            */}
            {freeLook && (
              <div
                style={{
                  fontSize: 10,
                  lineHeight: 1.6,
                  opacity: 0.5,
                  margin: '2px 0 8px 2px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                <div>W A S D pan · space up · shift down</div>
                <div>← → roll · ↑ ↓ pitch</div>
                <div>pinch / scroll zoom</div>
              </div>
            )}

            <Section label="size by">
              <Pill active={sizeBy === 'degree'} onClick={() => setSizeBy('degree')}>
                degree
              </Pill>
              <Pill active={sizeBy === 'rank'} onClick={() => setSizeBy('rank')}>
                rank
              </Pill>
              <Pill active={sizeBy === 'uniform'} onClick={() => setSizeBy('uniform')}>
                flat
              </Pill>
            </Section>

            <Section label="cluster by">
              {/* Advantage leads. It is the default and the only axis
                  that means something without prior vocabulary. */}
              <Pill
                active={clusterBy === 'advantage'}
                onClick={() => setClusterBy('advantage')}
              >
                advantage
              </Pill>
              {mode === 'position' && (
                <Pill
                  active={clusterBy === 'positionType'}
                  onClick={() => setClusterBy('positionType')}
                >
                  position type
                </Pill>
              )}
              <Pill active={clusterBy === 'posture'} onClick={() => setClusterBy('posture')}>
                posture
              </Pill>
              {mode === 'move' && (
                <>
                  <Pill
                    active={clusterBy === 'moveCategory'}
                    onClick={() => setClusterBy('moveCategory')}
                  >
                    move type
                  </Pill>
                  <Pill
                    active={clusterBy === 'submissionType'}
                    onClick={() => setClusterBy('submissionType')}
                  >
                    submission type
                  </Pill>
                  <Pill
                    active={clusterBy === 'disposition'}
                    onClick={() => setClusterBy('disposition')}
                  >
                    disposition
                  </Pill>
                </>
              )}
              <Pill active={clusterBy === 'family'} onClick={() => setClusterBy('family')}>
                family
              </Pill>
              <Pill active={clusterBy === 'concept'} onClick={() => setClusterBy('concept')}>
                concept
              </Pill>
            </Section>

            {/*
              A second level inside the first, not a replacement for it.
              Hidden when clustering is off or already BY family, since in
              both cases there is no primary region for a family to sit
              inside.
            */}
            {clusterBy !== 'none' && clusterBy !== 'family' && (
              <Section label="within each cluster">
                <Pill
                  active={familySubClusters}
                  onClick={() => setFamilySubClusters(!familySubClusters)}
                >
                  group families: {familySubClusters ? 'on' : 'off'}
                </Pill>
              </Section>
            )}

            {/* Cluster strength slider */}
            <div
              style={{
                marginTop: -4,
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: clusterBy === 'none' ? 0.35 : 1,
                transition: 'opacity 120ms ease',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  opacity: 0.55,
                  minWidth: 56,
                  letterSpacing: 0.4,
                }}
              >
                strength
              </span>
              <input
                type="range"
                min={0}
                max={2.5}
                step={0.05}
                value={clusterStrength}
                disabled={clusterBy === 'none'}
                onChange={(e) => setClusterStrength(parseFloat(e.target.value))}
                style={{
                  flex: 1,
                  accentColor: BLUE,
                  cursor: clusterBy === 'none' ? 'not-allowed' : 'pointer',
                }}
                aria-label="Cluster strength"
                title={`Cluster pull strength multiplier (${clusterStrength.toFixed(
                  2,
                )}× baseline)`}
              />
              <span
                style={{
                  fontSize: 10,
                  fontVariantNumeric: 'tabular-nums',
                  opacity: 0.6,
                  minWidth: 32,
                  textAlign: 'right',
                }}
              >
                {clusterStrength.toFixed(2)}×
              </span>
            </div>

            <Section label="difficulty">
              {DIFFICULTY_ORDER.map((d) => (
                <Pill
                  key={d}
                  active={difficultyFilter.has(d)}
                  onClick={() => toggleDifficulty(d)}
                >
                  {d}
                </Pill>
              ))}
            </Section>

            {/* Data export — dumps the full dataset (every position,
                move, counter with tags / descriptions / media refs)
                as a JSON file for offline review + curation. Async
                (reads from dataSource) so we track a tiny local
                "exporting" flag to disable the button mid-download
                and give feedback. */}
            <Section label="data">
              <Pill
                active={false}
                onClick={async () => {
                  if (exporting) return;
                  setExporting(true);
                  try {
                    await downloadDataExport();
                  } catch (err) {
                    console.error('Data export failed:', err);
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                {exporting ? 'exporting…' : 'export JSON'}
              </Pill>
            </Section>

            {/* Onboarding — replay the first-orbit coach-mark tour.
                Clears the seen-tour flag and flips tourRunning so the
                bubbles start again immediately (no front door anymore). */}
            <Section label="tour">
              <Pill
                active={false}
                onClick={() => {
                  flag.clear(SEEN_TOUR);
                  setOpen(false);
                  useAppStore.getState().setTourRunning(true);
                }}
              >
                replay tour
              </Pill>
            </Section>

            {/* Feedback + donate entry points moved OUT of settings:
                the "contribute" icon now lives in the top-bar
                (NavOverlay, next to search) and the Buy Me a Coffee
                icon sits next to the brand in the Hud. Both are more
                discoverable there than buried in the options panel. */}
          </div>,
          document.body,
      )}
    </>
  );
}

/** Section header + pill rack. Kept tight on vertical rhythm so the
 *  panel reads as one continuous control surface rather than a long
 *  stack of separated cards. */
function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          color: INK3,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function Pill({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...(active
          ? activePill(BLUE)
          : { background: 'transparent', color: INK2, border: `1px solid ${LINE}` }),
        borderRadius: RADIUS.sm,
        padding: '5px 10px',
        fontSize: 12,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLButtonElement).style.background = LINE;
          (e.currentTarget as HTMLButtonElement).style.borderColor = LINE2;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.borderColor = LINE;
        }
      }}
    >
      {children}
    </button>
  );
}
